'use client';

import PageRenderer from '@components/page-renderer/page-renderer.view';
import { usePreview } from 'cms/clients/sanity.preview';
import { DEFAULT_PAGE_MAPPER } from 'config/page-map';
import { usePathname } from 'next/navigation';
import contactInformationQuery from 'queries/contact-information-query';
import { pageQuery } from 'queries/page-queries';
import siteSettingsQuery from 'queries/site-settings-query';
import type { ReactElement } from 'react';
import React from 'react';
import type ContactInformation from 'types/contact-information';
import type { PageData } from 'types/page';
import type { SiteSettings } from 'types/site-settings';

function PreviewPageRenderer(): ReactElement {
  const pathname = usePathname();

  const pageData = usePreview<PageData>(pageQuery, {
    // Disabling because Next.js adds the | null type when the pages and app dir are present, but
    // we only use the pages directory for an API route
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-non-null-assertion
    slug: pathname!,
  });

  const siteSettings: SiteSettings | null =
    usePreview<SiteSettings>(siteSettingsQuery);

  const contactInformation: ContactInformation | null =
    usePreview<ContactInformation>(contactInformationQuery);

  if (siteSettings === null) {
    throw new Error('Preview renderer could not find siteSettings');
  }

  if (contactInformation === null) {
    throw new Error('Preview renderer could not find contactInformation');
  }

  if (pageData === null) {
    throw new Error(`Page not found: ${pathname}`);
  }

  return (
    <PageRenderer
      context={{
        contactInformation,
        siteSettings,
      }}
      page={
        pageData._type === 'page'
          ? {
              ...pageData,
              publishedDate: new Date(pageData.publishedDate),
            }
          : pageData
      }
      pageMapper={DEFAULT_PAGE_MAPPER}
    />
  );
}

export default PreviewPageRenderer;
